// frontend/src/hooks/useBulkOnlineAvailability.js
// Hook for checking online game availability across venues in bulk

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../config/api';
import { authService } from '../services/auth';
import { httpRequest } from '../utils/httpClient';
import logger from '../utils/logger';

export const useBulkOnlineAvailability = (gameIds, venueCode = null) => {
  const [availabilityResults, setAvailabilityResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Store previous game IDs to prevent unnecessary API calls
  const prevGameIdsRef = useRef([]);
  // Debounce timer ref: coalesces rapid gameIds changes from incremental scroll-loads
  const debounceTimerRef = useRef(null);

  const checkBulkAvailability = useCallback(async (idsToCheck) => {
    if (!idsToCheck || idsToCheck.length === 0) {
      setAvailabilityResults({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call the bulk backend endpoint with only game_ids
      const requestBody = {
        game_ids: idsToCheck,
        venue_code: venueCode,
      };

      const response = await httpRequest(
        `${API_BASE}/games/bulk-online-availability`,
        {
          method: 'POST',
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailabilityResults(data.results || {});
      } else {
        logger.warn('Failed to check bulk online availability:', response.status);
        setAvailabilityResults({});
      }
    } catch (err) {
      logger.error('Error checking bulk online availability:', err);
      setError(err.message);
      setAvailabilityResults({});
    } finally {
      setIsLoading(false);
    }
  }, [venueCode]);

  useEffect(() => {
    if (gameIds && gameIds.length > 0) {
      const currentIdsStr = JSON.stringify([...gameIds].sort());
      const prevIdsStr = JSON.stringify([...prevGameIdsRef.current].sort());

      if (currentIdsStr !== prevIdsStr) {
        prevGameIdsRef.current = [...gameIds];

        // Debounce: cancel any pending check and schedule a new one.
        // This coalesces rapid expansions (e.g. scroll-load increments) into one request.
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          checkBulkAvailability(prevGameIdsRef.current);
        }, 500);
      }
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [gameIds, checkBulkAvailability]);

  // Helper function to get availability for a specific game by ID
  const getGameAvailability = useCallback((gameId) => {
    return availabilityResults[gameId] || { venues: [] };
  }, [availabilityResults]);

  return {
    availabilityResults,
    getGameAvailability,
    isLoading,
    error,
    refetch: checkBulkAvailability
  };
};
